// מצב globals לעולם Caché (change connector-cache-globals-mode): המיפוי שמנהל בינה כותב לישות שנקראת ישירות מ-global של
// InterSystems Caché (תפנית), הצורה של שדה כזה (sourceColumn), ובקשת השליפה שהפלטפורמה שולחת לקונקטור במקום SQL.
// הקונקטור מריץ סקריפט ObjectScript **קבוע** (apps/connector/scripts/globals-extract.cos) עם הבקשה כפרמטרים; אין ObjectScript חופשי.
import { z } from 'zod';

/** תפקיד רמת subscript: company/const = ערך קבוע · period = חלון תקופות (YYMM) · key/line/free = נסרקים ($ORDER), line = מספריים בלבד. */
export const GlobalsLevelRole = z.enum(['company', 'period', 'key', 'line', 'const', 'free']);
export type GlobalsLevelRole = z.infer<typeof GlobalsLevelRole>;

export const GlobalsLevel = z.object({
  /** שם לתיעוד (אותיות קטנות); ההפניה משדה היא לפי מיקום הרמה (`s1`, `s2`, …). */
  name: z.string().regex(/^[a-z][a-z0-9_]*$/),
  role: GlobalsLevelRole,
  /** company/const: הערך הקבוע של ה-subscript (חובה). */
  value: z.string().min(1).optional(),
  /** רמה נסרקת: רק הילד הראשון/האחרון (למשל צובר החודש האחרון: `period` עם `last`). */
  pick: z.enum(['first', 'last']).optional(),
  /** רמה אחרונה עם pick: גם כשאין ילד, השורה של האב נפלטת (ערכי הרמה ריקים). */
  optional: z.boolean().optional(),
});
export type GlobalsLevel = z.infer<typeof GlobalsLevel>;

/** תקופה בפורמט YYMM (למשל 2512 = דצמבר 2025). */
export const Yymm = z.string().regex(/^\d{4}$/, 'period must be YYMM');

export const GlobalsMap = z
  .object({
    /** namespace של Caché (BIL / ACC / MLI …). */
    namespace: z.string().regex(/^[A-Za-z%][A-Za-z0-9_-]{0,30}$/),
    /** שם ה-global, עם או בלי `^` (RHZ / ^RHZ). */
    global: z.string().regex(/^\^?[A-Za-z%][A-Za-z0-9.]{0,30}$/),
    /** מפריד השדות בערך הצומת (תפנית: `*`). */
    separator: z.string().min(1).max(1).default('*'),
    levels: z.array(GlobalsLevel).min(1).max(8),
    /** חלון התקופות (חובה כשיש רמת period נסרקת): מ-YYMM ועד התקופה הנוכחית. */
    window: z.object({ from: Yymm }).optional(),
  })
  .superRefine((m, ctx) => {
    m.levels.forEach((l, i) => {
      if ((l.role === 'company' || l.role === 'const') && !l.value) ctx.addIssue({ code: 'custom', message: `level ${i + 1} (${l.role}) requires value`, path: ['levels', i, 'value'] });
      if (l.role === 'period' && l.value) ctx.addIssue({ code: 'custom', message: `level ${i + 1}: period level takes a window, not a value`, path: ['levels', i, 'value'] });
      if (l.optional && i !== m.levels.length - 1) ctx.addIssue({ code: 'custom', message: `level ${i + 1}: optional is allowed on the last level only`, path: ['levels', i, 'optional'] });
      if (l.optional && !l.pick) ctx.addIssue({ code: 'custom', message: `level ${i + 1}: optional requires pick`, path: ['levels', i, 'optional'] });
    });
    const periods = m.levels.filter((l) => l.role === 'period' && !l.pick);
    if (periods.length > 1) ctx.addIssue({ code: 'custom', message: 'at most one scanned period level', path: ['levels'] });
    if (periods.length === 1 && !m.window) ctx.addIssue({ code: 'custom', message: 'a scanned period level requires window.from (YYMM)', path: ['window'] });
  });
export type GlobalsMap = z.infer<typeof GlobalsMap>;

/** טיפוסי המקור של שדה globals (sourceType של השדה): המרה בקונקטור ל-date_ddmmyy ול-hebrew_visual בלבד; השאר כמחרוזות. */
export const GlobalsSourceType = z.enum(['text', 'number', 'date_ddmmyy', 'hebrew_visual', 'code']);
export type GlobalsSourceType = z.infer<typeof GlobalsSourceType>;

/**
 * sourceColumn של שדה בישות globals: `s<n>` = ה-subscript ברמה n · `p<n>` = piece n של ערך צומת העלה · `p<n>@<l>` = piece n של
 * ערך הצומת ברמה l · `=<ביטוי>` = חשבון על pieces של העלה (למשל `=p2+p4-p6`; + - * / וסוגריים בלבד).
 */
export type GlobalsSource = { kind: 'sub'; level: number } | { kind: 'piece'; piece: number; level: number | null } | { kind: 'expr'; expr: string };

export function parseGlobalsSource(src: string): GlobalsSource | null {
  const s = src.trim();
  let m = /^s(\d{1,2})$/i.exec(s);
  if (m) return { kind: 'sub', level: Number(m[1]) };
  m = /^p(\d{1,3})(?:@(\d{1,2}))?$/i.exec(s);
  if (m) return { kind: 'piece', piece: Number(m[1]), level: m[2] ? Number(m[2]) : null };
  if (s.startsWith('=')) {
    const expr = s.slice(1).trim();
    if (!expr || !/^[\sp\d+\-*/().]+$/i.test(expr) || !/p\d+/i.test(expr)) return null;
    return { kind: 'expr', expr };
  }
  return null;
}

/** רמה כפי שהקונקטור מקבל אותה: E = ערכים מנויים (חברה/קבוע/תקופות בחלון) · S = סריקה ($ORDER). */
export type GlobalsRequestLevel = { kind: 'E'; values: string[] } | { kind: 'S'; numeric?: boolean; pick?: 'F' | 'L'; optional?: boolean };
export type GlobalsRequestColumn = { name: string; src: string; type: GlobalsSourceType };
/** הבקשה שהפלטפורמה שולחת כ-"sql" של עבודת המקור (JSON): הקונקטור דוחה כל דבר אחר במצב globals. */
export type GlobalsRequest = {
  globals: { v: 1; op: 'extract'; namespace: string; global: string; sep: string; levels: GlobalsRequestLevel[]; columns: GlobalsRequestColumn[]; limit?: number };
};

/** YYMM של תאריך (לוח המקור). */
export function yymmOf(d: Date): string {
  return `${String(d.getUTCFullYear() % 100).padStart(2, '0')}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** YYMM קודם. */
export function previousYymm(p: string): string {
  const y = 2000 + Number(p.slice(0, 2));
  const m = Number(p.slice(2, 4));
  const d = new Date(Date.UTC(y, m - 2, 1));
  return yymmOf(d);
}

/** כל התקופות מ-from ועד to (כולל), בסדר עולה; from > to = ריק. */
export function periodsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let y = 2000 + Number(from.slice(0, 2));
  let m = Number(from.slice(2, 4));
  const ey = 2000 + Number(to.slice(0, 2));
  const em = Number(to.slice(2, 4));
  if (!(m >= 1 && m <= 12 && em >= 1 && em <= 12)) return out;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${String(y % 100).padStart(2, '0')}${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
    if (out.length > 1200) break;
  }
  return out;
}

/** האינדקס (1-based) של רמת התקופה הנסרקת, או null. */
export function globalsPeriodLevel(m: Pick<GlobalsMap, 'levels'>): number | null {
  const i = m.levels.findIndex((l) => l.role === 'period' && !l.pick);
  return i < 0 ? null : i + 1;
}

/**
 * בניית בקשת השליפה לישות × חברה × תקופות. `periods` = הערכים המנויים לרמת התקופה הנסרקת (null כשאין כזו).
 * רמת company מקבלת את הערך מהמיפוי (לא את קוד החברה בעולם: הקוד בעולם הוא תווית, ה-subscript הוא עובדה של המקור).
 */
export function globalsRequest(
  m: GlobalsMap,
  columns: Array<{ name: string; source: string; sourceType: string }>,
  periods: string[] | null,
  limit?: number,
): GlobalsRequest {
  const levels: GlobalsRequestLevel[] = m.levels.map((l) => {
    if (l.role === 'company' || l.role === 'const') return { kind: 'E', values: [l.value!] };
    if (l.role === 'period' && !l.pick) return { kind: 'E', values: periods ?? [] };
    const s: GlobalsRequestLevel = { kind: 'S' };
    if (l.role === 'line') s.numeric = true;
    if (l.pick) s.pick = l.pick === 'first' ? 'F' : 'L';
    if (l.optional) s.optional = true;
    return s;
  });
  return {
    globals: {
      v: 1, op: 'extract', namespace: m.namespace, global: m.global.startsWith('^') ? m.global : `^${m.global}`, sep: m.separator ?? '*', levels,
      columns: columns.map((c) => ({ name: c.name, src: c.source, type: (GlobalsSourceType.safeParse(c.sourceType).success ? c.sourceType : 'text') as GlobalsSourceType })),
      ...(limit ? { limit } : {}),
    },
  };
}

/**
 * מה השער והפרסום דורשים מישות globals (פונקציה טהורה, אותה בדיקה בשניהם): חברה עם ערך, חלון לרמת תקופה נסרקת, מקורות שדות
 * תקינים וייחודיים, רמות קיימות, ושדה שמחזיק את ה-subscript של התקופה (מפתח ההחלפה של הסבב). מחזיר הודעות בעברית.
 */
export function globalsIssues(entity: string, raw: unknown, fields: Array<{ key: string; source: string; sourceType: string }>): string[] {
  const pre = `הישות ${entity}:`;
  const parsed = GlobalsMap.safeParse(raw);
  if (!parsed.success) return [`${pre} מיפוי globals לא תקין: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`];
  const m = parsed.data;
  const issues: string[] = [];
  // ישות עסקית: חברה כ-subscript הראשון. מילון מערכת בלי ממד חברה (^ATAB(12,קוד), ^T("RSUGL",קוד)): ערך קבוע (const) ראשון.
  // בשני המקרים הרמה הראשונה מנויה; לעולם לא סריקה של כל ה-global מהשורש (חוצת-חברות).
  const first = m.levels[0];
  if (!first || (first.role !== 'company' && first.role !== 'const'))
    issues.push(`${pre} ה-subscript הראשון חייב להיות חברה (role company עם value) בישות עסקית, או ערך קבוע (const) במילון מערכת; אין סריקה חוצת-חברות`);
  if (m.levels.some((l) => l.role === 'company') && first?.role !== 'company') issues.push(`${pre} רמת החברה חייבת להיות הראשונה (ה-subscript הראשון ב-global)`);
  const periodLevel = globalsPeriodLevel(m);
  const seen = new Map<string, string>();
  for (const f of fields) {
    const s = parseGlobalsSource(f.source);
    if (!s) {
      issues.push(`${pre} sourceColumn '${f.source}' של השדה ${f.key} לא תקין. הצורה: s<n> (subscript ברמה n) · p<n> (piece של העלה) · p<n>@<l> (piece ברמה l) · =p2+p4-p6 (חשבון)`);
      continue;
    }
    if (!GlobalsSourceType.safeParse(f.sourceType).success) issues.push(`${pre} sourceType '${f.sourceType}' של השדה ${f.key} לא מוכר. מותר: text, number, date_ddmmyy, hebrew_visual, code`);
    const lvl = s.kind === 'sub' ? s.level : s.kind === 'piece' ? s.level : null;
    if (lvl !== null && (lvl < 1 || lvl > m.levels.length)) issues.push(`${pre} השדה ${f.key} מפנה לרמה ${lvl} אבל במיפוי ${m.levels.length} רמות`);
    const norm = f.source.trim().toLowerCase().replace(/\s+/g, '');
    const dup = seen.get(norm);
    if (dup) issues.push(`${pre} השדות ${dup} ו-${f.key} מצביעים על אותו מקור (${f.source}); pieces חייבים להיות ייחודיים`);
    else seen.set(norm, f.key);
  }
  if (periodLevel !== null && !fields.some((f) => /^s(\d+)$/i.test(f.source.trim()) && Number(f.source.trim().slice(1)) === periodLevel))
    issues.push(`${pre} יש רמת תקופה נסרקת (רמה ${periodLevel}) אבל אין שדה עם sourceColumn s${periodLevel}; התקופה חייבת להיות עמודה (מפתח ההחלפה של הסבב)`);
  return issues;
}
